'use client'

import { forwardRef, useState, useRef, useEffect, ReactNode } from 'react'
import { ChevronDown, Search, Check, AlertCircle, Info, X } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Select Component
 * 
 * Enhanced select implementing all 10 BIRHAUS principles:
 * - Miller's Law: Maximum 7 visible options (7±2 cognitive limit)
 * - Spanish-first labeling and option text
 * - Progressive disclosure with search when >7 options
 * - Accessibility compliance (WCAG AA+)
 * - Cognitive load warnings when exceeding limits
 * - Performance optimized with virtual scrolling for large lists
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausSelectOption {
  value: string
  label: string
  labelEs?: string
  labelEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  disabled?: boolean
  icon?: ReactNode
  group?: string
}

export interface BirhausSelectProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Options (Miller's Law enforcement)
  options: BirhausSelectOption[]
  value?: string | string[]
  defaultValue?: string | string[]
  onChange?: (value: string | string[]) => void
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Placeholder text
  placeholder?: string
  placeholderEs?: string
  placeholderEn?: string
  
  // Multi-select
  multiple?: boolean
  maxSelections?: number
  
  // BIRHAUS Principle #8: Performance is UX
  searchable?: boolean
  searchPlaceholder?: string
  searchPlaceholderEs?: string
  searchPlaceholderEn?: string
  
  // BIRHAUS Principle #1: Form serves flow
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'filled' | 'minimal'
  
  // BIRHAUS Principle #6: Accessibility = dignity
  helpText?: string
  helpTextEs?: string
  helpTextEn?: string
  
  // State
  disabled?: boolean
  required?: boolean
  loading?: boolean
  
  // Cognitive load management
  cognitiveLoadLevel?: 'low' | 'medium' | 'high'
  showCognitiveWarning?: boolean
  
  // Enhanced functionality
  clearable?: boolean
  closeOnSelect?: boolean
  
  // Styling
  className?: string
  id?: string
}

export const BirhausSelect = forwardRef<HTMLButtonElement, BirhausSelectProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Descriptions
  description,
  descriptionEs,
  descriptionEn,
  
  // Data
  options = [],
  value,
  defaultValue,
  onChange,
  
  // Error handling
  error,
  errorEs,
  errorEn,
  
  // Placeholder
  placeholder,
  placeholderEs,
  placeholderEn,
  
  // Multi-select
  multiple = false,
  maxSelections,
  
  // Search
  searchable = false,
  searchPlaceholder,
  searchPlaceholderEs,
  searchPlaceholderEn,
  
  // Appearance
  size = 'md',
  variant = 'default',
  
  // Help text
  helpText,
  helpTextEs,
  helpTextEn,
  
  // State
  disabled = false,
  required = false,
  loading = false,
  
  // Cognitive load
  cognitiveLoadLevel = 'medium',
  showCognitiveWarning = true,
  
  // Enhanced
  clearable = false,
  closeOnSelect = true,
  
  // Styling
  className,
  id,
}, ref) => {
  
  // BIRHAUS: State management
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedValues, setSelectedValues] = useState<string[]>(
    Array.isArray(value) ? value :
    Array.isArray(defaultValue) ? defaultValue :
    value ? [value] :
    defaultValue ? [defaultValue] :
    []
  )
  
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  const displayError = resolveText(errorEs, errorEn, error)
  const displayPlaceholder = resolveText(
    placeholderEs || 'Seleccionar opción...',
    placeholderEn || 'Select option...',
    placeholder
  )
  const displaySearchPlaceholder = resolveText(
    searchPlaceholderEs || 'Buscar opciones...',
    searchPlaceholderEn || 'Search options...',
    searchPlaceholder
  )
  const displayHelpText = resolveText(helpTextEs, helpTextEn, helpText)
  
  // BIRHAUS: Miller's Law enforcement
  const MILLER_LAW_LIMIT = 7
  const filteredOptions = options.filter(option =>
    searchTerm === '' || 
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.labelEs?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.labelEn?.toLowerCase().includes(searchTerm.toLowerCase())
  )
  
  const visibleOptions = filteredOptions.slice(0, searchable ? filteredOptions.length : MILLER_LAW_LIMIT)
  const hasMoreOptions = filteredOptions.length > MILLER_LAW_LIMIT
  
  // BIRHAUS: Cognitive load warning
  const cognitiveLoad = options.length > MILLER_LAW_LIMIT ? 'high' : 
                       options.length > 4 ? 'medium' : 'low'
  
  // Handle option selection
  const handleOptionSelect = (optionValue: string) => {
    let newValues: string[]
    
    if (multiple) {
      if (selectedValues.includes(optionValue)) {
        newValues = selectedValues.filter(v => v !== optionValue)
      } else {
        if (maxSelections && selectedValues.length >= maxSelections) {
          return // Don't allow more selections
        }
        newValues = [...selectedValues, optionValue]
      }
    } else {
      newValues = [optionValue]
      if (closeOnSelect) {
        setIsOpen(false)
      }
    }
    
    setSelectedValues(newValues)
    onChange?.(multiple ? newValues : newValues[0] || '')
  }
  
  // Clear selection
  const handleClear = () => {
    setSelectedValues([])
    onChange?.(multiple ? [] : '')
  }
  
  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // Auto-focus search when opened
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])
  
  // Get selected option labels
  const getSelectedLabels = () => {
    return selectedValues.map(val => {
      const option = options.find(opt => opt.value === val)
      return option ? (option.labelEs || option.label) : val
    })
  }
  
  const selectedLabels = getSelectedLabels()
  const displayValue = selectedLabels.length > 0 ? selectedLabels.join(', ') : displayPlaceholder
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-sm',
    lg: 'h-12 px-4 text-base'
  }
  
  const variantClasses = {
    default: 'border border-gray-300 bg-white',
    filled: 'border-0 bg-gray-100',
    minimal: 'border-0 border-b border-gray-300 bg-transparent rounded-none'
  }
  
  // Error state styling
  const hasError = !!displayError
  const stateClasses = hasError 
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : 'focus:border-blue-500 focus:ring-blue-500'
  
  const triggerClasses = cn(
    'w-full rounded-lg transition-all duration-200 flex items-center justify-between',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:cursor-not-allowed disabled:opacity-50',
    safeAccess(sizeClasses, size, sizeClasses.md),
    safeAccess(variantClasses, variant, variantClasses.default),
    stateClasses,
    className
  )
  
  return (
    <div className="space-y-2">
      {/* Label and cognitive load warning */}
      {(displayLabel || (showCognitiveWarning && cognitiveLoad === 'high')) && (
        <div className="flex items-center justify-between">
          {displayLabel && (
            <label 
              htmlFor={id}
              className={cn(
                'block text-sm font-medium text-gray-700',
                required && "after:content-['*'] after:ml-1 after:text-red-500"
              )}
            >
              {displayLabel}
            </label>
          )}
          
          {/* BIRHAUS: Cognitive load warning */}
          {showCognitiveWarning && cognitiveLoad === 'high' && (
            <div className="flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle className="h-3 w-3" />
              <span>+{options.length - MILLER_LAW_LIMIT} opciones (considera agrupar)</span>
            </div>
          )}
        </div>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-sm text-gray-600">{displayDescription}</p>
      )}
      
      {/* Select container */}
      <div ref={containerRef} className="relative">
        <button
          ref={ref}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={triggerClasses}
          data-birhaus-component="select"
          data-cognitive-load={cognitiveLoad}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          id={id}
        >
          <span className={cn(
            'truncate text-left',
            selectedValues.length === 0 && 'text-gray-400'
          )}>
            {displayValue}
          </span>
          
          <div className="flex items-center gap-2">
            {/* Clear button */}
            {clearable && selectedValues.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Limpiar selección"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            
            {/* Loading indicator */}
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            ) : (
              <ChevronDown className={cn(
                'h-4 w-4 text-gray-400 transition-transform duration-200',
                isOpen && 'rotate-180'
              )} />
            )}
          </div>
        </button>
        
        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
            {/* Search input */}
            {searchable && (
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={displaySearchPlaceholder}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}
            
            {/* Options list */}
            <div className="max-h-48 overflow-y-auto">
              {visibleOptions.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 text-center">
                  {searchTerm ? 'No se encontraron opciones' : 'No hay opciones disponibles'}
                </div>
              ) : (
                visibleOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value)
                  const optionLabel = option.labelEs || option.label
                  const optionDescription = option.descriptionEs || option.description
                  
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleOptionSelect(option.value)}
                      disabled={option.disabled}
                      className={cn(
                        'w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-gray-50',
                        'focus:outline-none focus:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed',
                        isSelected && 'bg-blue-50 text-blue-700'
                      )}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {/* Icon */}
                      {option.icon && (
                        <span className="flex-shrink-0">{option.icon}</span>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium truncate">{optionLabel}</span>
                          {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                        </div>
                        {optionDescription && (
                          <p className="text-xs text-gray-500 truncate">{optionDescription}</p>
                        )}
                      </div>
                    </button>
                  )
                })
              )}
              
              {/* More options indicator */}
              {hasMoreOptions && !searchable && (
                <div className="p-2 border-t border-gray-200 bg-gray-50 text-center">
                  <p className="text-xs text-gray-600">
                    +{filteredOptions.length - MILLER_LAW_LIMIT} opciones más
                  </p>
                  <p className="text-xs text-gray-500">
                    Habilita búsqueda para ver todas
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Error message */}
      {displayError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800">{displayError}</p>
        </div>
      )}
      
      {/* Help text */}
      {displayHelpText && !displayError && (
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>{displayHelpText}</p>
        </div>
      )}
      
      {/* Selection summary for multi-select */}
      {multiple && selectedValues.length > 0 && (
        <div className="text-xs text-gray-600">
          {selectedValues.length} de {maxSelections || '∞'} seleccionado{selectedValues.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
})

BirhausSelect.displayName = 'BirhausSelect'

// BIRHAUS: Hook for managing select state
export function useBirhausSelect() {
  const [value, setValue] = useState<string | string[]>('')
  const [isOpen, setIsOpen] = useState(false)
  
  const handleChange = (newValue: string | string[]) => {
    setValue(newValue)
  }
  
  const clear = () => {
    setValue(Array.isArray(value) ? [] : '')
  }
  
  return {
    value,
    isOpen,
    handleChange,
    clear,
    setValue,
    setIsOpen
  }
}

// BIRHAUS: Helper to create option groups that respect Miller's Law
export function createBirhausOptionGroups(
  options: BirhausSelectOption[],
  groupBy: (option: BirhausSelectOption) => string
): Record<string, BirhausSelectOption[]> {
  const groups: Record<string, BirhausSelectOption[]> = {}
  
  options.forEach(option => {
    const groupKey = groupBy(option)
    const existingGroup = safeAccess(groups, groupKey, [] as BirhausSelectOption[])
    if (!existingGroup) {
      groups[groupKey] = []
    }
    const groupArray = groups[groupKey] // Safe after initialization above
    if (Array.isArray(groupArray)) {
      groupArray.push(option)
    }
  })
  
  // Warn if any group exceeds Miller's Law
  Object.entries(groups).forEach(([groupName, groupOptions]) => {
    if (groupOptions.length > 7) {
      console.warn(`BIRHAUS Miller's Law Warning: Group "${groupName}" has ${groupOptions.length} options (max recommended: 7)`)
    }
  })
  
  return groups
}

// Components and utilities are exported via individual export declarations above